import React from 'react'
import { isEqual, get } from 'lodash'
import { createSelector } from 'reselect'
import { connect } from 'react-redux'
import { withRouter, Redirect } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { bindActionCreators } from 'redux'
import PropTypes from 'prop-types'
import queryString from 'query-string'
import { SelectLayout } from '@blazar-plugins/device'
import featuresPlugin from '@blazar-plugins/features'
import { actionCreators as searchInputActionCreators } from 'reduxBits/searchInput'

import getEmojiForCategory from 'utils/getEmojiForCategory'
import { setCurrentSearch } from 'utils/adBack'

import { actionCreators as accountActionCreators } from 'reduxBits/account'
import { actionCreators as searchParamsActionCreators } from 'reduxBits/searchParams'
import {
  actionCreators as classifiedActionCreators,
  selectors as classifiedSelectors,
} from 'reduxBits/classified'

import ArchivedAdNotificationProvider from '../List/ArchivedAdNotification/ArchivedAdNotificationProvider'
import SmallLayout from './SmallLayout'
import MediumLayout from './MediumLayout'

const cgParameterSelector = createSelector(
  (state) => get(state, ['searchParams', 'byQueryKey', 'cg']),
  (cg) => cg,
)

export class _List extends React.Component {
  constructor(props) {
    super(props)
    this.onUnload = () => {
      setCurrentSearch(this.props.location, { searchResults: this.props.searchResults })
    }
  }

  componentDidMount() {
    this.props.searchParamsActions.updateActiveParameters().then(() => {
      this.props.classifiedActions.fetchAds(queryString.parse(this.props.location.search))
    })

    if (this.props.loggedIn) {
      this.props.accountActions.fetchAdWatches()
    }

    window.addEventListener('unload', this.onUnload)
  }

  UNSAFE_componentWillUpdate(nextProps) {
    if (isEqual(this.props.location.search, nextProps.location.search)) {
      return
    }

    this.props.searchParamsActions.updateActiveParameters().then(() => {
      this.props.classifiedActions.fetchAds(queryString.parse(nextProps.location.search))
    })
  }

  componentWillUnmount() {
    this.onUnload()
    window.removeEventListener('unload', this.onUnload)
  }

  render() {
    const {
      error,
      ads,
      haveSearchResults,
      isFetching,
      searchTotal,
      totalPageCount,
      firstFetch,
      location,
      searchTitle,
    } = this.props

    const queryParams = queryString.parse(location.search)
    const currentPage = Number(get(queryParams, 'page', 1))

    if (currentPage > totalPageCount && totalPageCount > 0) {
      const newQuery = { ...queryParams, page: totalPageCount }
      const newUrl = `${location.pathname}?${queryString.stringify(newQuery)}`

      return <Redirect to={newUrl} />
    }

    let searchResultsMetaTags

    if (!(firstFetch || isFetching || error)) {
      const hasCategory = Boolean(queryParams.cg)
      const hasRegion = Boolean(queryParams.r)
      const emoji = getEmojiForCategory(queryParams.cg)
      const descriptionEmoji = emoji ? `${emoji} ` : ''

      let descriptionText = `${descriptionEmoji}Blocket är Sveriges största marknadsplats`

      if (hasCategory) {
        descriptionText = `${descriptionText} inom kategorin ${searchTitle.toLowerCase()}`
      }

      if (hasCategory || !hasRegion) {
        const hitsText = searchTotal && searchTotal >= 9 ? ` med över ${searchTotal} annonser` : ''
        descriptionText = `${descriptionText}${hitsText}`
      }
      searchResultsMetaTags = [
        <title key="title">{searchTitle} - Blocket</title>,
        <meta key="og:title" property="og:title" content={`${searchTitle} - Blocket`} />,
        <meta key="description" name="description" content={descriptionText} />,
        <meta key="og:description" property="og:description" content={descriptionText} />,
      ]
    }

    return (
      <React.Fragment>
        <Helmet>
          {searchResultsMetaTags}
          {(ads.length === 0 || !haveSearchResults) && <meta name="robots" content="noindex" />}
        </Helmet>
        <ArchivedAdNotificationProvider>
          <SelectLayout
            layouts={{
              small: () => <SmallLayout {...this.props} searchResultDescription={searchTitle} />,
              medium: () => <MediumLayout {...this.props} searchResultDescription={searchTitle} />,
            }}
          />
        </ArchivedAdNotificationProvider>
      </React.Fragment>
    )
  }
}

_List.propTypes = {
  error: PropTypes.bool.isRequired,
  errorId: PropTypes.string,
  haveSearchResults: PropTypes.bool,
  ads: PropTypes.array.isRequired,
  isFetching: PropTypes.bool.isRequired,
  searchTotal: PropTypes.number.isRequired,
  totalPageCount: PropTypes.number.isRequired,
  firstFetch: PropTypes.bool.isRequired,
  classifiedActions: PropTypes.object.isRequired,
  accountActions: PropTypes.object.isRequired,
  searchParamsActions: PropTypes.object.isRequired,
  loggedIn: PropTypes.bool,
  location: PropTypes.object.isRequired,
  inventory: PropTypes.object,
  searchResults: PropTypes.object,
  urls: PropTypes.object.isRequired,
  searchTitle: PropTypes.string,
  breadcrumbs: PropTypes.array,
  searchParametersDefinition: PropTypes.object,
  searchSubsetDescription: PropTypes.string,
  cgParameterDefinition: PropTypes.shape({}),
}

const mapStateToProps = (state) => ({
  searchResults: state.classified.searchResults,
  ads: state.classified.searchResults.ads,
  inventory: state.classified.searchResults.inventory,
  searchTitle: state.classified.searchResults.searchTitle,
  error: state.classified.searchResults.error,
  errorId: state.classified.searchResults.errorId,
  firstFetch: state.classified.searchResults.firstFetch,
  haveSearchResults: state.classified.searchResults.searchTotal > 0,
  isFetching: state.classified.searchResults.isFetching,
  searchTotal: state.classified.searchResults.searchTotal,
  totalPageCount: state.classified.searchResults.totalPageCount,
  searchSubsetDescription: state.classified.searchResults.searchSubsetDescription,
  hitsPerPage: classifiedSelectors.numAdsPerPage(state),
  urls: state.config.urls,
  breadcrumbs: state.quickass.breadcrumbs,
  searchParametersDefinition: state.searchParams.byQueryKey,
  cgParameterDefinition: cgParameterSelector(state),
  searchInputActions: PropTypes.shape({
    updateHistory: PropTypes.func.isRequired,
  }),
  geoPref: state.geoPicker.geoPreference,
  isRefreshEnabled: featuresPlugin.selectors.isEnabled(state, 'refreshButton'),
  experimentalIPCEnabled: featuresPlugin.selectors.isEnabled(state, 'experimental-ipc-placement'),
  filterQuery: state.searchParams.filterQuery,
})

const mapDispatchToProps = (dispatch) => ({
  classifiedActions: bindActionCreators(classifiedActionCreators, dispatch),
  accountActions: bindActionCreators(accountActionCreators, dispatch),
  searchParamsActions: bindActionCreators(searchParamsActionCreators, dispatch),
  searchInputActions: bindActionCreators(searchInputActionCreators, dispatch),
})

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(_List),
)
